import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from 'react-select/async';
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

const CATEGORIES = [
  { value: 'domain', label: 'Domain' },
  { value: 'software', label: 'Software' },
  { value: 'platform-transfer', label: 'Platform Transfer' },
  { value: 'other', label: 'Other' },
];

export default function CreateExpensePage() {
  const [accounts, setAccounts]   = useState([]);
  const [created, setCreated]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);
  const [formError, setFormError] = useState(null);

  const [expense, setExpense] = useState({
    accountID: "", category: "", customCategory: "", amount: "", notes: "",
    transactionDate: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/account`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setAccounts(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch accounts");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'transactionDate') {
      setExpense(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setExpense(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAccountSelect = (opt) => {
    setExpense(prev => ({ ...prev, accountID: opt ? opt.value : "" }));
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setCreated(false);

    if (!expense.category) return setFormError("Please select a category.");
    if (expense.category === 'other' && !expense.customCategory.trim()) return setFormError("Please describe the expense category.");
    if (!expense.amount || Number(expense.amount) <= 0) return setFormError("Enter an amount greater than 0.");
    if (!expense.transactionDate) return setFormError("Please select a date.");

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/transaction/create`,
        {
          accountID: expense.accountID || null,
          type: 'expense',
          category: expense.category,
          customCategory: expense.category === 'other' ? expense.customCategory.trim() : undefined,
          revenue: 0,
          profit: -Math.abs(Number(expense.amount)),
          notes: expense.notes,
          transactionDate: expense.transactionDate + "",
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCreated(true);
      setExpense({ accountID: "", category: "", customCategory: "", amount: "", notes: "", transactionDate: null });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create expense");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Create Expense" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Log an Expense</div>
              <div className="form-panel-subtitle">Domain renewals, software, platform transfers and other costs</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Category & Amount</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-tag" />Category <span className="required-star">*</span></label>
                    <select className="form-input" name="category" value={expense.category} onChange={handleChange} required>
                      <option value="">Select category…</option>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  {expense.category === 'other' && (
                    <div className="form-field" style={{ marginTop: 10 }}>
                      <label className="form-field-label">Describe the category <span className="required-star">*</span></label>
                      <input className="form-input" type="text" name="customCategory" value={expense.customCategory} onChange={handleChange} placeholder="e.g. VPS hosting" />
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Amount <span className="required-star">*</span></label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="amount" value={expense.amount} onChange={handleChange} step="0.01" min="0" required />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Details</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Related Account (optional)</label>
                    <AsyncSelect
                      cacheOptions defaultOptions
                      loadOptions={loadOptions}
                      onChange={handleAccountSelect}
                      placeholder="Search for an account…"
                      isClearable
                      styles={getSelectStyles()}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Date <span className="required-star">*</span></label>
                    <input className="form-input" type="date" name="transactionDate"
                      value={formatDateForInput(expense.transactionDate)} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-12">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={expense.notes || ''}
                      onChange={handleChange} rows={3} placeholder="Additional notes…"
                      style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {created    && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Expense logged successfully!</div>}
              {formError  && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{formError}</div>}
              {error      && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-plus-circle'}`} />
                  {submitting ? 'Logging…' : 'Log Expense'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
